import { useMemo, useState } from 'react'
import { useTableColumns } from '@/hooks/useTableColumns'
import { useSortedRows } from '@/hooks/useSortedRows'
import type { TableColumnDef } from '@/lib/tableColumnPrefs'
import type { SortAccessors } from '@/lib/tableSort'
import { ColumnCells, ResizableTh } from '@/components/table'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Loader2, Trash2, Plus, AlertCircle } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { SearchableSelect } from '@/components/ui/searchable-select'
import { formsApi } from '@/api/formsApi'
import { cellKey } from '@/lib/tableForm'
import {
  buildSelector,
  hasIncompleteCondition,
  describeMappingTarget,
  formatSelector,
  mappingFieldOptions,
  tableColumnOptions,
  tableFieldById,
  tableRowOptions,
} from '@/lib/mappingTargets'
import { versionLabel } from '@/lib/versioning'
import {
  formPopulationApi,
  type CreateMappingPayload,
  type FormFieldMapping,
} from '@/api/formPopulationApi'
import { FEATURES } from '@/lib/featureFlags'
import { useFeatureFlag } from '@/providers/FeatureFlagsProvider'
import { FeatureDisabledNote } from '@/components/auth/FeatureDisabledNote'

/**
 * Per-integration mapping authoring, rendered as a tab on the integration detail
 * page. The integration fixes the instrument result variant (its `resultType`), so
 * there is no "Source" picker — you pick a form, then map its fields to THIS
 * instrument's value fields. Lists/creates/deletes `FormFieldMapping`s.
 */
// Per-user column layout. Source field and Form field are locked: a mapping row
// is the pair, so hiding either makes the row meaningless. See
// src/components/table/index.ts for the pattern.
const MAPPINGS_TABLE_ID = 'integration-mappings'

const MAPPINGS_COLUMNS: TableColumnDef[] = [
  { id: 'sourceField', header: 'Source field', locked: true, sortable: true },
  { id: 'formField', header: 'Form field', locked: true, sortable: true },
  { id: 'condition', header: 'Condition', sortable: true },
  { id: 'label', header: 'Label', sortable: true },
  { id: 'actions', header: 'Actions', locked: true },
]

export function IntegrationMappingsTab({
  integrationId,
  tenantId,
}: {
  integrationId: string
  tenantId: string | null
}) {
  const cols = useTableColumns(MAPPINGS_TABLE_ID, MAPPINGS_COLUMNS)
  const queryClient = useQueryClient()
  const formEngineOn = useFeatureFlag(FEATURES.formEngine)

  // Resolve which instrument variant this integration produces (DB-driven, via the
  // resultType → Source.key bridge exposed by /sources). No hardcoded list.
  const { data: sources = [] } = useQuery({
    queryKey: ['instrument-sources', tenantId],
    queryFn: () => formPopulationApi.getSources(tenantId as string),
    enabled: !!tenantId,
  })
  const source = sources.find((s) => s.id === integrationId)
  const resultType = source?.resultType
  // The EPIC/EHR integration is the source of patient.new demographics, so it can
  // ALSO author `patient` mappings (form field <- demographic key) alongside its
  // instrument (epic order) mappings.
  const isEhr = resultType === 'epic'

  const { data: catalog } = useQuery({
    queryKey: ['value-fields'],
    queryFn: formPopulationApi.getValueFields,
  })
  const { data: defsResult } = useQuery({
    queryKey: ['form-definitions'],
    queryFn: () => formsApi.listDefinitions(),
  })
  const definitions = defsResult?.items ?? []

  const [formId, setFormId] = useState('')
  const [targetFieldId, setTargetFieldId] = useState('')
  // Row and column of a table field's cell; both empty for a flat field.
  const [rowId, setRowId] = useState('')
  const [colId, setColId] = useState('')

  // Each choice narrows the next, so clear everything downstream of a change.
  function chooseForm(id: string) {
    setFormId(id)
    setTargetFieldId('')
    setRowId('')
    setColId('')
  }
  function chooseField(id: string) {
    setTargetFieldId(id)
    setRowId('')
    setColId('')
  }
  function chooseRow(id: string) {
    setRowId(id)
    setColId('')
  }
  const { data: selectedForm } = useQuery({
    queryKey: ['form-definition', formId],
    queryFn: () => formsApi.getDefinition(formId),
    enabled: !!formId,
  })

  const fieldOptions = useMemo(
    () => (selectedForm ? mappingFieldOptions(selectedForm) : []),
    [selectedForm],
  )
  const tableField = selectedForm && targetFieldId ? tableFieldById(selectedForm, targetFieldId) : null
  const rowOptions = tableRowOptions(tableField)
  const columnOptions = tableColumnOptions(tableField, rowId)

  const { data: allMappings = [], isLoading: mappingsLoading } = useQuery({
    queryKey: ['instrument-mappings', formId],
    queryFn: () => formPopulationApi.listMappings(formId),
    enabled: !!formId,
  })
  // Patient demographics is the default on the EHR integration. Non-EHR integrations
  // (the toggle is hidden for them) only ever author instrument mappings.
  const [mappingKind, setMappingKind] = useState<'instrument' | 'patient'>('patient')
  const activeKind: 'instrument' | 'patient' = isEhr ? mappingKind : 'instrument'

  // Show this integration's variant: instrument mappings match its resultType;
  // patient mappings are source-agnostic (authored here on the EHR integration).
  const mappings = allMappings.filter((m) =>
    activeKind === 'patient'
      ? m.sourceKind === 'patient'
      : m.sourceKind !== 'patient' && m.resultType === resultType,
  )
  // Column sorting (z8nrz7e4h6). Form field sorts by the label it reads as,
  // which needs the selected form, hence built here rather than at module level.
  const mappingSort = useMemo<SortAccessors<FormFieldMapping>>(
    () => ({
      sourceField: (m) => m.valueField,
      formField: (m) => describeMappingTarget(selectedForm, m.targetFieldId).display,
      condition: (m) => {
        const text = formatSelector(m.selector)
        return text === '—' ? null : text
      },
      label: (m) => m.label,
    }),
    [selectedForm],
  )
  const sortedMappings = useSortedRows(mappings, cols.sort, mappingSort)

  const [valueField, setValueField] = useState('')
  const [label, setLabel] = useState('')
  // Condition rows: a result is used only when every key equals its value.
  const [conditions, setConditions] = useState([{ key: '', value: '' }])
  const [error, setError] = useState<string | null>(null)

  const valueFieldOptions =
    activeKind === 'patient'
      ? catalog?.patient ?? []
      : resultType
        ? catalog?.[resultType] ?? []
        : []

  const createMut = useMutation({
    mutationFn: (payload: CreateMappingPayload) => formPopulationApi.createMapping(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['instrument-mappings', formId] })
      setTargetFieldId('')
      setRowId('')
      setColId('')
      setValueField('')
      setLabel('')
      setConditions([{ key: '', value: '' }])
      setError(null)
    },
    onError: (e) => setError(e instanceof Error ? e.message : 'Failed to create mapping.'),
  })

  const deleteMut = useMutation({
    mutationFn: (id: string) => formPopulationApi.deleteMapping(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['instrument-mappings', formId] }),
  })

  function handleCreate() {
    if (!formId || !targetFieldId || !valueField) {
      setError('Pick a form, a source field, and a form field.')
      return
    }
    if (tableField && (!rowId || !colId)) {
      setError('Pick a row and a column.')
      return
    }
    // A half-filled condition is refused rather than dropped. `buildSelector`
    // keeps only rows with both halves, so a key typed without its value used to
    // vanish — and a mapping with no selector matches EVERY result the
    // instrument sends, the opposite of what the operator was narrowing to.
    if (hasIncompleteCondition(conditions)) {
      setError('Finish each condition, or clear it: a condition needs both a key and a value.')
      return
    }
    const target = tableField ? cellKey(rowId, colId) : targetFieldId
    const selector = buildSelector(conditions)
    createMut.mutate(
      activeKind === 'patient'
        ? {
            formDefinitionId: formId,
            targetFieldId: target,
            sourceKind: 'patient',
            valueField,
            label: label.trim() || undefined,
          }
        : {
            formDefinitionId: formId,
            targetFieldId: target,
            sourceKind: 'instrument',
            resultType,
            valueField,
            ...(selector ? { selector } : {}),
            label: label.trim() || undefined,
          },
    )
  }

  // Mappings bind an instrument value to a FORM FIELD, and both the definition
  // list and the mapping store live behind `formEngine`. Without it the form
  // picker is empty and every save 403s, so the tab has nothing to offer even
  // though `integrations` itself is on.
  if (!formEngineOn) {
    return (
      <div className="py-12">
        <FeatureDisabledNote label="The form engine" className="mx-auto max-w-md" />
      </div>
    )
  }
  if (!tenantId) {
    return (
      <div className="text-center py-12 text-sm text-muted-foreground">
        Select a tenant to manage mappings.
      </div>
    )
  }
  if (!resultType) {
    return (
      <div className="text-center py-12 text-sm text-muted-foreground">
        This integration doesn’t produce mappable instrument results.
      </div>
    )
  }

  return (
    <div>
      <p className="text-sm text-muted-foreground mb-4">
        Map a <strong>{source?.name}</strong> source field to a form field. Once mapped,
        “Populate from instrument” proposes that value on the form.
      </p>

      {/* Form picker (searchable — there may be thousands of forms) */}
      <div className="grid gap-1.5 max-w-md mb-6">
        <Label>Form</Label>
        <SearchableSelect
          value={formId}
          onChange={chooseForm}
          options={definitions.map((d) => ({ value: d.id, label: `${d.name} (${d.versionName ?? versionLabel(d.version)})` }))}
          placeholder="Select a form"
        />
      </div>

      {!formId ? (
        <div className="text-center py-12 text-sm text-muted-foreground">
          Select a form to view and create its mappings.
        </div>
      ) : (
        <>
          {/* Create mapping */}
          <div className="rounded-lg border p-4 mb-6">
            <h3 className="text-sm font-semibold mb-3">Add a mapping</h3>
            {isEhr && (
              <div className="grid gap-1.5 mb-3 max-w-xs">
                <Label className="text-xs">Mapping source</Label>
                <Select
                  value={mappingKind}
                  onValueChange={(v) => {
                    setMappingKind(v as 'instrument' | 'patient')
                    setValueField('')
                    setError(null)
                  }}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="patient">Patient demographics (patient.new)</SelectItem>
                    <SelectItem value="instrument">Instrument result (EPIC order)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
            {error && (
              <div className="flex items-center gap-2 text-sm text-destructive mb-3">
                <AlertCircle className="h-4 w-4 shrink-0" />
                {error}
              </div>
            )}
            <div className="grid gap-4 md:grid-cols-3">
              <div className="grid gap-1.5">
                <Label className="text-xs">Source field</Label>
                <Select value={valueField || undefined} onValueChange={setValueField}>
                  <SelectTrigger>
                    <SelectValue placeholder="Source field" />
                  </SelectTrigger>
                  <SelectContent>
                    {valueFieldOptions.map((vf) => (
                      <SelectItem key={vf} value={vf}>
                        {vf}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="grid gap-1.5">
                <Label className="text-xs">Form field</Label>
                <Select value={targetFieldId || undefined} onValueChange={chooseField}>
                  <SelectTrigger>
                    <SelectValue placeholder="Field" />
                  </SelectTrigger>
                  <SelectContent>
                    {fieldOptions.map((f) => (
                      <SelectItem key={f.id} value={f.id}>
                        {f.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="grid gap-1.5">
                <Label className="text-xs">Label (optional)</Label>
                <Input
                  placeholder="e.g. NC-200 viability"
                  value={label}
                  onChange={(e) => setLabel(e.target.value)}
                />
              </div>
            </div>

            {tableField && (
              rowOptions.length === 0 ? (
                <p className="mt-4 text-sm text-muted-foreground">
                  This table has no predefined rows. Pick a table with rows to map into it.
                </p>
              ) : (
                <div className="grid gap-4 md:grid-cols-3 mt-4">
                  <div className="grid gap-1.5">
                    <Label className="text-xs">Row</Label>
                    <Select value={rowId || undefined} onValueChange={chooseRow}>
                      <SelectTrigger>
                        <SelectValue placeholder="Row" />
                      </SelectTrigger>
                      <SelectContent>
                        {rowOptions.map((r) => (
                          <SelectItem key={r.id} value={r.id}>
                            {r.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="grid gap-1.5">
                    <Label className="text-xs">Column</Label>
                    <Select value={colId || undefined} onValueChange={setColId} disabled={!rowId}>
                      <SelectTrigger>
                        <SelectValue placeholder={rowId ? 'Column' : 'Pick a row first'} />
                      </SelectTrigger>
                      <SelectContent>
                        {columnOptions.map((c) => (
                          <SelectItem key={c.id} value={c.id}>
                            {c.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              )
            )}

            {activeKind === 'instrument' && (
              <div className="mt-4 grid gap-1.5">
                <Label className="text-xs">Condition (optional)</Label>
                <p className="text-xs text-muted-foreground">
                  Match results where this instrument field equals this value. Leave empty to use
                  any result.
                </p>
                <datalist id={`mapping-condition-keys-${integrationId}`}>
                  {valueFieldOptions.map((vf) => (
                    <option key={vf} value={vf} />
                  ))}
                </datalist>
                {conditions.map((c, i) => (
                  <div key={i} className="flex items-center gap-2 max-w-xl">
                    <Input
                      placeholder="Instrument field"
                      list={`mapping-condition-keys-${integrationId}`}
                      value={c.key}
                      onChange={(e) =>
                        setConditions((prev) =>
                          prev.map((p, j) => (j === i ? { ...p, key: e.target.value } : p)),
                        )
                      }
                    />
                    <span className="text-sm text-muted-foreground">=</span>
                    <Input
                      placeholder="Value"
                      value={c.value}
                      onChange={(e) =>
                        setConditions((prev) =>
                          prev.map((p, j) => (j === i ? { ...p, value: e.target.value } : p)),
                        )
                      }
                    />
                    <Button
                      size="sm"
                      variant="ghost"
                      aria-label="Remove condition"
                      onClick={() =>
                        setConditions((prev) =>
                          prev.length > 1 ? prev.filter((_, j) => j !== i) : [{ key: '', value: '' }],
                        )
                      }
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                ))}
                <div>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setConditions((prev) => [...prev, { key: '', value: '' }])}
                  >
                    <Plus className="h-3.5 w-3.5 mr-2" />
                    Add condition
                  </Button>
                </div>
              </div>
            )}
            <div className="mt-4">
              <Button size="sm" onClick={handleCreate} disabled={createMut.isPending}>
                {createMut.isPending ? (
                  <Loader2 className="h-3.5 w-3.5 mr-2 animate-spin" />
                ) : (
                  <Plus className="h-3.5 w-3.5 mr-2" />
                )}
                Add mapping
              </Button>
            </div>
          </div>

          {/* Existing mappings */}
          {mappingsLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
            </div>
          ) : mappings.length === 0 ? (
            <div className="text-center py-12 text-sm text-muted-foreground">
              No mappings for this form yet. Add one above.
            </div>
          ) : (
            <>
            <div className="border rounded-lg overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-table-header border-b">
                  <tr>
                    {cols.visible.map(({ def }) => (
                      <ResizableTh
                        key={def.id}
                        columnId={def.id}
                        columns={cols}
                        label={def.header}
                        className={
                          def.id === 'actions'
                            ? 'px-4 py-3'
                            : 'text-left px-4 py-3 font-medium text-table-header-foreground'
                        }
                        resizable={def.id !== 'actions'}
                      >
                        {def.header}
                      </ResizableTh>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {sortedMappings.map((m) => {
                    const target = describeMappingTarget(selectedForm, m.targetFieldId)
                    return (
                    <tr key={m.id} className="border-b last:border-0">
                      <ColumnCells
                        columns={cols}
                        cells={{
                          sourceField: <td className="px-4 py-3 text-muted-foreground">{m.valueField}</td>,
                          formField: (
                            <td className="px-4 py-3 font-medium">
                              {target.display}
                              {target.stale && (
                                <Badge variant="outline" className="ml-2 font-normal text-muted-foreground">
                                  No longer on this form
                                </Badge>
                              )}
                            </td>
                          ),
                          condition: (
                            <td className="px-4 py-3 text-muted-foreground">{formatSelector(m.selector)}</td>
                          ),
                          label: <td className="px-4 py-3 text-muted-foreground">{m.label ?? '—'}</td>,
                          actions: (
                            <td className="px-4 py-3 text-right">
                              <Button
                                size="sm"
                                variant="outline"
                                className="text-destructive hover:text-destructive"
                                onClick={() => deleteMut.mutate(m.id)}
                                disabled={deleteMut.isPending}
                              >
                                <Trash2 className="h-3.5 w-3.5" />
                              </Button>
                            </td>
                          ),
                        }}
                      />
                    </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
            </>
          )}
        </>
      )}
    </div>
  )
}
